import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { blogAPI } from '../lib/api';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const categoryLabels = {
  'land-registration': { bn: 'জমি রেজিস্ট্রেশন', en: 'Land Registration' },
  'land-records': { bn: 'ভূমি রেকর্ড', en: 'Land Records' },
  'legal-tips': { bn: 'আইনি পরামর্শ', en: 'Legal Tips' },
  'fees-taxes': { bn: 'ফি ও কর', en: 'Fees & Taxes' },
  'land-info': { bn: 'ভূমি তথ্য', en: 'Land Info' },
};

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const isBn = language === 'bn';

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await blogAPI.getAll();
        setPosts(res.data);
      } catch (err) {
        console.error('Failed to fetch blog posts:', err);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === 'all' ? posts : posts.filter(p => p.category === selectedCategory);
  const categories = ['all', ...new Set(posts.map(p => p.category))];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SEO 
        title={isBn ? 'ভূমি ব্লগ - জমি সংক্রান্ত তথ্য ও গাইড' : 'Land Blog - Information & Guides'}
        description={isBn 
          ? 'বাংলাদেশের জমি রেজিস্ট্রেশন, নামজারি, খতিয়ান, দলিল সম্পর্কে বিস্তারিত আর্টিকেল ও গাইড পড়ুন।'
          : 'Read detailed articles and guides about land registration, Namjari, Khatian, and deeds in Bangladesh.'
        }
        path="/blog"
      />
      <Navbar isDark={isDark} />
      
      {/* Hero Section */}
      <div className="bg-[#2E7D32] text-white py-12 md:py-16 mt-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen size={32} />
            <h1 className="text-3xl md:text-4xl font-bold" data-testid="blog-page-title">
              {isBn ? 'ভূমি ব্লগ' : 'Land Blog'}
            </h1>
          </div>
          <p className="text-lg text-white/80 max-w-2xl">
            {isBn ? 'বাংলাদেশের ভূমি সংক্রান্ত গুরুত্বপূর্ণ তথ্য, গাইড এবং টিপস' : 'Important information, guides and tips about land in Bangladesh'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat
                  ? 'bg-[#2E7D32] text-white'
                  : isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat === 'all' ? (isBn ? 'সব' : 'All') : (categoryLabels[cat]?.[language] || cat)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className={`group rounded-xl overflow-hidden border transition-all hover:shadow-lg ${
                  isDark ? 'bg-gray-800 border-gray-700 hover:border-[#2E7D32]' : 'bg-white border-gray-200 hover:border-[#2E7D32]'
                }`}
                data-testid={`blog-card-${post.slug}`}
              >
                <div className="h-2 bg-[#2E7D32]" />
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary" className="text-xs">
                      {categoryLabels[post.category]?.[language] || post.category}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Eye size={12} />
                      <span>{post.views}</span>
                    </div>
                  </div>
                  <h2 className={`font-bold text-lg mb-2 group-hover:text-[#2E7D32] transition-colors leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {isBn ? post.title : (post.title_en || post.title)}
                  </h2>
                  <p className={`text-sm mb-4 line-clamp-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isBn ? post.excerpt : (post.excerpt_en || post.excerpt)}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Calendar size={12} />
                      <span>{new Date(post.created_at).toLocaleDateString(isBn ? 'bn-BD' : 'en-US')}</span>
                    </div>
                    <span className="text-[#2E7D32] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      {isBn ? 'পড়ুন' : 'Read'} <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{isBn ? 'কোনো আর্টিকেল পাওয়া যায়নি' : 'No articles found'}</p>
          </div>
        )}
      </div>
      
      <Footer isDark={isDark} />
    </div>
  );
};

export default BlogList;
